import { useState } from "react";
import { api } from "../lib/api";
import { buzz } from "../lib/haptics";
import {
  NEXT_OPTIONS, nextSeasonLabel, readTally, shareText,
  type NextChoice, type VoteTally,
} from "../shared/next-season";

// The end of a season, and the decision that follows it.
//
// Three described choices, not a redirect loop. A member says what they want and
// sees it recorded; the host sees the tally and decides, because a crew is not a
// democracy and somebody still has to run it.
//
// The offline exits are deliberately separate optional buttons rather than an
// assumed next step. Settling up is between the crew and nothing to do with the
// app, and planning something is the kind of thing that happens in the group
// chat or not at all.

interface Props {
  leagueId: string;
  crewName: string;
  championName?: string;
  isHost: boolean;
  tally?: VoteTally;
  seasonsPlayed?: number;
  myVote?: NextChoice | null;
  onHome: () => void;
}

export function WhatsNext({
  leagueId, crewName, championName, isHost, tally, seasonsPlayed, myVote = null, onHome,
}: Props) {
  const [choice, setChoice] = useState<NextChoice | null>(myVote);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const label = nextSeasonLabel(seasonsPlayed);

  async function vote(c: NextChoice) {
    setSaving(true); setErr("");
    try {
      await api(`/api/league/admin`, { body: { op: "vote-next", leagueId, choice: c } });
      setChoice(c);
      buzz("commit");
    } catch (e: any) {
      setErr(e?.message ?? "That didn't save. Try again.");
    } finally {
      setSaving(false);
    }
  }

  // Native share, so the conversation lands where the crew already talks. There
  // is no chat in this app on purpose.
  async function share(kind: "settle" | "trip" | "champion") {
    const text = shareText(kind, crewName, championName);
    try {
      if (navigator.share) await navigator.share({ text });
      else { await navigator.clipboard.writeText(text); setErr(""); }
    } catch { /* dismissed the sheet, which is not a failure */ }
  }

  return (
    <div className="grid" style={{ gap: 14 }}>
      <div className="card live bloom">
        <span className="tag">That&apos;s the season</span>
        <p className="close-line">Thanks for showing up for each other.</p>
        <p className="muted">
          Everything above happened because people took a minute to say what was going on with
          them. That is the whole game.
        </p>
      </div>

      <div className="card">
        <span className="tag">Do y&apos;all want to run {label}?</span>
        <p className="muted">Say what you&apos;d want. Your host takes it from there.</p>
        <div className="grid" style={{ gap: 8 }}>
          {NEXT_OPTIONS.map((o) => (
            <button
              key={o.key}
              className={`choice${choice === o.key ? " current" : ""}`}
              aria-pressed={choice === o.key}
              disabled={saving}
              onClick={() => void vote(o.key)}
            >
              <span className="choice-title">{o.title}</span>
              <span className="choice-blurb">{o.blurb}</span>
            </button>
          ))}
        </div>
        {choice && <p className="muted saved-note">Got it. You can change this any time.</p>}
        {err && <p className="error">{err}</p>}
      </div>

      {isHost && tally && (
        <div className="card">
          <span className="tag">{readTally(tally).headline}</span>
          <p className="muted">{readTally(tally).detail}</p>
          <p className="muted" style={{ fontSize: 13 }}>
            You see the counts, never who picked what. That keeps an honest answer cheap to give.
          </p>
        </div>
      )}

      <div className="card">
        <span className="tag">Take it to the group</span>
        <p className="muted">The talking happens where it always happens.</p>
        <button className="btn ghost" onClick={() => void share("champion")}>Share the result</button>
        <button className="btn ghost" onClick={() => void share("settle")}>Settle up</button>
        <button className="btn ghost" onClick={() => void share("trip")}>Plan the next link up</button>
      </div>

      <button className="btn primary" onClick={onHome}>Back to the crew</button>
    </div>
  );
}
